import base from 'axios';
import { configs } from './app.config';
import { stopTokenRefresh } from './utils/tokenRefreshManager';

const api = base.create({
  baseURL: configs.apiUrl,
  withCredentials: true
});

let isRefreshing = false;
let failedQueue = [];
let onTokenRefreshed = null;
let isLoggingOut = false;

export const setTokenRefreshCallback = (callback) => {
  onTokenRefreshed = callback;
};

export const setLoggingOut = (value) => {
  isLoggingOut = value;
};

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.request.use(
  (config) => {
    if (
      isLoggingOut &&
      !config.url?.includes('api/User/logout') &&
      !config.url?.includes('api/User/refresh')
    ) {
      const error = new Error('Logout in progress');
      error.isLoggingOut = true;
      error.config = config;
      return Promise.reject(error);
    }
    
    const newConfig = config;
    if (localStorage.token || sessionStorage.token) {
      newConfig.headers.Authorization = `Bearer ${sessionStorage.token ? sessionStorage.token : localStorage.token}`;
    }
    return newConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.isLoggingOut) {
      return new Promise(() => {});
    }
    
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes('api/User/refresh')) {
        isLoggingOut = true;
        stopTokenRefresh();
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await api.post('api/User/refresh');
        const newToken = response.data?.data?.token || response.data?.token;

        if (!newToken) {
          throw new Error('No token in refresh response');
        }

        if (localStorage.getItem('token')) {
          localStorage.setItem('token', newToken);
        } else if (sessionStorage.getItem('token')) {
          sessionStorage.setItem('token', newToken);
        }

        if (onTokenRefreshed) {
          onTokenRefreshed(newToken);
        }

        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isLoggingOut = true;
        stopTokenRefresh();
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
